import { ReactNode, useCallback, useMemo, useRef } from 'react';
import { useSelector } from 'react-redux';
import { selectEventId } from '../../../redux/slices/config';
import { selectStreamGoRoom } from '../../../redux/slices/room';
import { StreamingView } from '../../../redux/streamGoApi/enums';
import { RoomAudioRenderer } from '../../LiveKit/RoomAudioRenderer';
import { useGetPlatformEventDetails, useGetRoom } from '../../Rooms';
import { useIsStreamingModeValid } from '../../StreamingViews';
import { PipPosition, StreamType } from '../enums';
import {
  PinnedView,
  PipView,
  PlaylistVideoView,
  PostrollView,
  PrerollView,
  SideBySideView,
  SingleTrackView,
  RtmpView,
} from '../views';
import { GridView } from '../views/GridView/GridView';
import { useGetSettings } from './useGetSettings';
import { usePlaylist } from './usePlaylist';

const mutedViews = [
  StreamingView.PreRoll,
  StreamingView.PostRoll,
  StreamingView.FullScreenSlidesNoSound,
];

const startEndViews = [StreamingView.PostRoll, StreamingView.PreRoll];

interface IGetStreamingView {
  isRtmp: boolean | undefined;
  streamingView: StreamingView | undefined;
}

const getStreamingView = ({ isRtmp, streamingView }: IGetStreamingView) => {
  if (isRtmp) {
    const isNotStartOrEndView = !startEndViews.includes(
      streamingView as StreamingView,
    );
    if (isNotStartOrEndView) {
      return StreamingView.Rtmp;
    }
  }

  return streamingView ?? StreamingView.WebCamsActiveSpeaker;
};

export const useGetStreamingTemplate = (roomId: string) => {
  const eventId = useSelector(selectEventId);
  const { pollInterval, postRollVideoUrl, preRollVideoUrl } = useGetSettings();
  const interval = pollInterval ? Number(pollInterval) : 5000;
  useGetRoom(roomId);
  const video = useRef<HTMLVideoElement>(null);
  const data = useSelector(selectStreamGoRoom);

  const { data: eventData } = useGetPlatformEventDetails(eventId, interval);
  const playlist = eventData?.playlist;
  const locale = eventData?.locale ?? 'en';

  const streamingView = data?.StreamingView;
  const defaultView = data?.StreamingViewDefault;
  const isRtmp = data?.IsRtmp;

  const { isMediaMode, nextPlaylistItem, onPlaying } = usePlaylist({
    playlist,
    eventId,
    defaultView,
    video: video?.current,
  });

  useIsStreamingModeValid({
    streamingView,
    roomId,
    isMediaMode: Boolean(isMediaMode) ?? false,
  });

  const SideBySide = useCallback(
    () => <SideBySideView streamingView={streamingView} />,
    [streamingView],
  );

  const Component = useMemo(() => {
    const videoComponents: Record<StreamingView, () => ReactNode> = {
      [StreamingView.PreRoll]: () => (
        <PrerollView
          locale={locale}
          webcamGoPreRollVideoUrl={preRollVideoUrl}
        />
      ),
      [StreamingView.PostRoll]: () => (
        <PostrollView
          locale={locale}
          webcamGoPostRollVideoUrl={postRollVideoUrl}
        />
      ),
      [StreamingView.Unset]: () => (
        <SingleTrackView streamType={StreamType.Webcam} />
      ),
      [StreamingView.WebCamsActiveSpeaker]: () => (
        <SingleTrackView streamType={StreamType.Webcam} />
      ),
      [StreamingView.WebCamsActiveSpeakerPinned]: () => (
        <PinnedView streamType={StreamType.Webcam} />
      ),
      [StreamingView.WebCamsGrid]: () => <GridView />,
      [StreamingView.ScreenshareWebcamSplit11Wrsl]: SideBySide,
      [StreamingView.ScreenshareWebcamSplit169Wrsl]: SideBySide,
      [StreamingView.ScreenshareWebCamSplit8020Wrsl]: SideBySide,
      [StreamingView.SlidesWebcamSplit11Wrsl]: SideBySide,
      [StreamingView.SlidesWebcamSplit169Wrsl]: SideBySide,
      [StreamingView.SlidesWebCamSplit8020Wrsl]: SideBySide,
      [StreamingView.FullScreenSlides]: () => (
        <SingleTrackView streamType={StreamType.Slides} />
      ),
      [StreamingView.FullScreenSlidesNoSound]: () => (
        <SingleTrackView streamType={StreamType.Slides} />
      ),
      [StreamingView.SlidesPinned]: () => (
        <PinnedView streamType={StreamType.Slides} />
      ),
      [StreamingView.FullScreenScreenShare]: () => (
        <SingleTrackView streamType={StreamType.Screenshare} />
      ),
      [StreamingView.ScreenSharePinned]: () => (
        <PinnedView streamType={StreamType.Screenshare} />
      ),
      [StreamingView.SlidesWebCamPipTopRight]: () => (
        <PipView position={PipPosition.Top} streamType={StreamType.Slides} />
      ),
      [StreamingView.SlidesWebCamPipBottomRight]: () => (
        <PipView position={PipPosition.Bottom} streamType={StreamType.Slides} />
      ),
      [StreamingView.ScreenshareWebCamPipTopRight]: () => (
        <PipView
          position={PipPosition.Top}
          streamType={StreamType.Screenshare}
        />
      ),
      [StreamingView.ScreenshareWebCamPipBottomRight]: () => (
        <PipView
          position={PipPosition.Bottom}
          streamType={StreamType.Screenshare}
        />
      ),
      [StreamingView.Rtmp]: () => <RtmpView />,
    };

    const isMuted = !streamingView || mutedViews.includes(streamingView);
    const view = getStreamingView({ isRtmp, streamingView });
    const TemplateView = videoComponents[view];

    const RenderedComponent = () => (
      <>
        <TemplateView />
        <RoomAudioRenderer
          isRtmp={isRtmp}
          muted={isMuted}
          volume={isMuted ? 0 : 1}
        />
      </>
    );

    return RenderedComponent;
  }, [
    SideBySide,
    streamingView,
    isRtmp,
    locale,
    preRollVideoUrl,
    postRollVideoUrl,
  ]);

  if (!streamingView) {
    return null;
  }

  if (
    isMediaMode &&
    playlist &&
    ![StreamingView.PostRoll, StreamingView.PreRoll].includes(streamingView)
  ) {
    return (
      <PlaylistVideoView
        isMediaMode={isMediaMode}
        nextPlaylistItem={nextPlaylistItem}
        onPlaying={onPlaying}
        playlist={playlist}
        video={video}
      />
    );
  }

  return <Component />;
};
